import type { TypedEvents } from "@repo/bridge";
import { EVENTS } from "@repo/bridge";
import { createCredManager, type CredManager } from "@repo/auth";
import type { Creds } from "@repo/auth";
import { createHostContext, type HostContext } from "@repo/bridge";
import { config } from "./config";

const DEV_CREDS_KEY = "__tigerAgentCreds";

/**
 * In standalone mode (no host bridge), reads a credential bundle from localStorage so the app
 * can be driven from devtools without a rebuild:
 *
 *   localStorage.setItem('__tigerAgentCreds', JSON.stringify({ token, userId, orgId }))
 *   // then reload
 *
 * Returns undefined when the key is absent or the payload is malformed; the caller falls back to
 * a rejecting fetchCreds in that case.
 */
function devFetchCreds(): (() => Promise<Creds>) | undefined {
  try {
    const raw = localStorage.getItem(DEV_CREDS_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<Creds>;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.userId !== "string" ||
      typeof parsed.orgId !== "string"
    ) {
      console.warn(
        "[tiger-agent] __tigerAgentCreds is malformed — expected { token, userId, orgId }",
      );
      return undefined;
    }
    // Strip expiresAt so the CredManager treats these creds as always fresh.
    const { token, userId, orgId } = parsed;
    return () => Promise.resolve({ token, userId, orgId });
  } catch {
    return undefined;
  }
}

/** Normalized host capabilities the rest of the app consumes instead of probing `window`. */
export interface HostState {
  platform: "ios" | "android" | "web" | "standalone";
  version: string | null;
  /** Present only when the web host is running inside the Desktop App (Electron or the legacy .NET wrapper). */
  desktopVersion?: string;
  protocolVersion: number | null;
  supportedEvents: string[];
  /** Whether the handshake succeeded (false in standalone/dev). */
  bridged: boolean;
  /** Capability check used to gate features on what the host actually wired up. */
  supports(event: string): boolean;
}

export interface BootstrapDeps {
  events: TypedEvents;
  spaVersion: string;
  /** Factory for the cred manager; injectable for tests. */
  createManager?: typeof createCredManager;
}

export interface BootstrapResult {
  host: HostState;
  creds: CredManager;
  /** Last-value cache of host context (conversation, theme, …), keyed by context type. */
  hostContext: HostContext;
}

const STANDALONE_HOST: Omit<HostState, "supports"> = {
  platform: "standalone",
  version: null,
  protocolVersion: null,
  supportedEvents: [],
  bridged: false,
};

function toHostState(base: Omit<HostState, "supports">): HostState {
  const supported = new Set(base.supportedEvents);
  return { ...base, supports: (event) => supported.has(event) };
}

/**
 * Single host-startup flow (per design doc 0007): handshake → record capabilities → warm
 * credentials for the initial org → subscribe to context changes. The cred manager is wired to
 * the bridge here — the only place `@repo/bridge` and `@repo/auth` meet — by injecting
 * `events.getCreds` as the manager's `fetchCreds`.
 */
export async function bootstrap(deps: BootstrapDeps): Promise<BootstrapResult> {
  const { events, spaVersion } = deps;
  const makeManager = deps.createManager ?? createCredManager;

  // Attached before the handshake so no context push is dropped while it (and the first-open
  // credential pull below) is in flight, and so the standalone path — which returns early — still
  // tracks context. `onContextUpdate` only registers a local handler, so there is nothing to
  // capability-gate here. The theme seed comes from `?theme=`; the host may override it any time.
  const hostContext = createHostContext(events, { initial: { theme: config.theme } });

  let host: HostState;
  try {
    const hs = await events.handshake(spaVersion);
    host = toHostState({
      platform: hs.hostPlatform,
      version: hs.hostVersion,
      desktopVersion: hs.hostDesktopVersion,
      protocolVersion: hs.protocolVersion,
      supportedEvents: hs.supportedEvents,
      bridged: true,
    });
  } catch {
    const fetchCreds =
      (!config.isProduction ? devFetchCreds() : undefined) ??
      (() => Promise.reject(new Error("no host bridges")));
    return {
      host: toHostState(STANDALONE_HOST),
      creds: makeManager({ fetchCreds }),
      hostContext,
    };
  }

  const creds = makeManager({ fetchCreds: () => events.getCreds() });

  if (host.supports(EVENTS.getCreds)) {
    // First-open pull: establishes the active org and warms the cache.
    await creds.getValidToken();

    // Seed the warmed org (unless the host already pushed one) so `useOrgChange` can tell a
    // switch from the initial value.
    const activeOrg = creds.getActiveOrg();
    if (activeOrg && !hostContext.channel("org").get()) {
      hostContext.set("org", activeOrg);
    }
  }

  if (host.supports(EVENTS.contextUpdate)) {
    events.onContextUpdate((ctx) => {
      if (ctx.type === "org") creds.setActiveOrg(ctx.id);
    });
  }

  return { host, creds, hostContext };
}
